import {
  dateWithinWindow,
  formatTimestamp,
  isTimestampEqual,
} from 'src/Utils/formatDate'
import { SCHEDULE_RATE_UNIT } from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import {
  EARLIEST_EVIDENCE_MODE,
  GetDateLimitResponse,
} from '@trustero/trustero-api-web/lib/accountsettings/accountsettings_pb'
import {
  DateLimitConfigType,
  DateLimitOptions,
  DateLimitRecommendations,
  MAX_SLIDING_MONTHS,
  MIN_SLIDING_MONTHS,
  RECOMMENDED_SLIDING_MONTHS,
} from './AccountSettings.constants'

const getSecondsForUnit = (unit: SCHEDULE_RATE_UNIT): number => {
  switch (unit) {
    case SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_DAYS:
      return 24 * 60 * 60
    case SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_HOURS:
      return 60 * 60
    case SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_MINUTES:
      return 60
    default:
      return 0
  }
}

export const convertRateToSeconds = (
  rateValue: number,
  rateUnit: SCHEDULE_RATE_UNIT,
): number => rateValue * getSecondsForUnit(rateUnit)

// getSlidingMonths returns the account's sliding window length, or 0 when it is on a fixed
// date. Every surface that renders the earliest evidence date needs this to word itself
// correctly, so derive it here rather than repeating the mode check at each one.
export const getSlidingMonths = (
  dateLimitInfo?: GetDateLimitResponse,
): number => {
  const policy = dateLimitInfo?.getPolicy()
  return policy?.getMode() === EARLIEST_EVIDENCE_MODE.EARLIEST_EVIDENCE_SLIDING
    ? policy.getSlidingMonths()
    : 0
}

// getDateLimitRecommendationsConfig builds the rows the earliest-evidence-date modal
// offers, and says which one is currently in force.
//
// The selected row comes from the stored policy, not from comparing dates: a sliding
// account's effective date matches none of the fixed choices, and inferring the selection
// from it would render a sliding account as though it had picked a one-off date.
export const getDateLimitRecommendationsConfig = ({
  createdAt,
  dateLimit,
  earliestOpenAuditStartDate,
  isSliding,
  slidingMonths,
  fixedDate,
  offerSliding,
}: {
  createdAt: Timestamp
  // The date in force right now — today's boundary for a sliding account.
  dateLimit: Timestamp
  earliestOpenAuditStartDate?: Timestamp
  isSliding: boolean
  slidingMonths: number
  // The stored fixed date, kept while sliding so switching back restores it.
  fixedDate: Timestamp
  offerSliding: boolean
}): DateLimitRecommendations => {
  const dateLimitConfig: DateLimitConfigType = {} as DateLimitConfigType

  // At most one row is highlighted. A sliding window is the better answer whenever it is
  // on offer and the account is not already on one, which leaves the older
  // earliest-open-audit suggestion for accounts that cannot slide yet.
  const recommended = getRecommendedDateLimitOption({
    earliestOpenAuditStartDate,
    isSliding,
    fixedDate,
    offerSliding,
  })

  if (offerSliding || isSliding) {
    dateLimitConfig[DateLimitOptions.SLIDING_WINDOW] = {
      title: DateLimitOptions.SLIDING_WINDOW,
      dateLabel: isSliding ? formatTimestamp(dateLimit) : '',
      dateValue: null,
      isRecommended: recommended === DateLimitOptions.SLIDING_WINDOW,
    }
  }

  dateLimitConfig[DateLimitOptions.ACCOUNT_CREATION] = {
    title: DateLimitOptions.ACCOUNT_CREATION,
    dateLabel: formatTimestamp(createdAt),
    dateValue: createdAt,
    isRecommended: false,
  }

  if (earliestOpenAuditStartDate) {
    dateLimitConfig[DateLimitOptions.EARLIEST_OPEN_AUDIT] = {
      title: DateLimitOptions.EARLIEST_OPEN_AUDIT,
      dateLabel: formatTimestamp(earliestOpenAuditStartDate),
      dateValue: earliestOpenAuditStartDate,
      isRecommended: recommended === DateLimitOptions.EARLIEST_OPEN_AUDIT,
    }
  }

  // The account's own date, when it is neither of the shortcuts above. Shown even while
  // sliding, since it is what switching back to a fixed date would restore.
  const matchesAShortcut =
    isTimestampEqual(fixedDate, createdAt) ||
    (!!earliestOpenAuditStartDate &&
      isTimestampEqual(fixedDate, earliestOpenAuditStartDate))
  if (!matchesAShortcut) {
    dateLimitConfig[DateLimitOptions.CURRENT_BOUNDARY] = {
      title: DateLimitOptions.CURRENT_BOUNDARY,
      dateLabel: formatTimestamp(fixedDate),
      dateValue: fixedDate,
      isRecommended: false,
    }
  }

  return {
    initialValue: getSelectedDateLimitOption({
      createdAt,
      earliestOpenAuditStartDate,
      isSliding,
      fixedDate,
    }),
    recommendations: dateLimitConfig,
    slidingMonths: slidingMonths || RECOMMENDED_SLIDING_MONTHS,
  } as DateLimitRecommendations
}

// getRecommendedDateLimitOption picks the single row to highlight, or null for none.
const getRecommendedDateLimitOption = ({
  earliestOpenAuditStartDate,
  isSliding,
  fixedDate,
  offerSliding,
}: {
  earliestOpenAuditStartDate?: Timestamp
  isSliding: boolean
  fixedDate: Timestamp
  offerSliding: boolean
}): DateLimitOptions | null => {
  if (offerSliding && !isSliding) {
    return DateLimitOptions.SLIDING_WINDOW
  }
  // Only worth suggesting when the account is not already there and the audit is recent
  // enough that its start makes a sensible boundary.
  if (
    !isSliding &&
    !!earliestOpenAuditStartDate &&
    !isTimestampEqual(earliestOpenAuditStartDate, fixedDate) &&
    dateWithinWindow(earliestOpenAuditStartDate, 365)
  ) {
    return DateLimitOptions.EARLIEST_OPEN_AUDIT
  }
  return null
}

const getSelectedDateLimitOption = ({
  createdAt,
  earliestOpenAuditStartDate,
  isSliding,
  fixedDate,
}: {
  createdAt: Timestamp
  earliestOpenAuditStartDate?: Timestamp
  isSliding: boolean
  fixedDate: Timestamp
}): DateLimitOptions => {
  if (isSliding) {
    return DateLimitOptions.SLIDING_WINDOW
  }
  if (isTimestampEqual(fixedDate, createdAt)) {
    return DateLimitOptions.ACCOUNT_CREATION
  }
  if (
    !!earliestOpenAuditStartDate &&
    isTimestampEqual(fixedDate, earliestOpenAuditStartDate)
  ) {
    return DateLimitOptions.EARLIEST_OPEN_AUDIT
  }
  return DateLimitOptions.CURRENT_BOUNDARY
}

// slidingMonthOptions lists 1..36 for the sliding-window dropdown, flagging the
// recommended value.
export const slidingMonthOptions = (): BasicDropdownItem[] =>
  Array.from(
    { length: MAX_SLIDING_MONTHS - MIN_SLIDING_MONTHS + 1 },
    (_, index) => {
      const months = MIN_SLIDING_MONTHS + index
      const unit = months === 1 ? 'month' : 'months'
      return {
        name:
          months === RECOMMENDED_SLIDING_MONTHS
            ? `${months} ${unit} (recommended)`
            : `${months} ${unit}`,
        value: months,
        isBold: months === RECOMMENDED_SLIDING_MONTHS,
      }
    },
  )
